import { Fragment, useState, useEffect, useRef, useCallback } from "react";
import OuterBodyModal from "./OuterBodyModal";
import { Row, Col, Form, Button } from "react-bootstrap";
import axios from "axios";
import styles from "./SearchCustomerModal.module.css";
import AddEditVendor from "./AddEditVendor";
import AddEditCustomer from "./AddEditCustomer";
import { API_BASE_URL } from "../../config";

const SearchCustomerModal = ({
  modalTitle,
  type,
  showModal,
  handleClose,
  updateFormData,
}) => {
  const initialFormData = {
    id: "",
    type: type === "purchase" ? "vendor" : "client",
    name: "",
    account_code: "",
    phone_no: "",
    alt_phone_no: "",
    email: "",
    address: "",
    gstin: "",
    state: "",
    city: "",
    opening_balance: "",
    debit_credit: {
      value: type === "purchase" ? "CREDIT" : "DEBIT",
      label: "purchase" ? "CREDIT" : "DEBIT",
    },
    validationError: "",
  };

  const [searchInput, setSearchInput] = useState("");
  const [searchType, setSearchType] = useState(type);
  const [searchResults, setSearchResults] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeRow, setActiveRow] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const tableRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  let osValue = 0;

  const onHandleSearchInput = async () => {
    // Don't search if input is too short
    if (!searchInput || searchInput.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/searchCustomersByInput`,
        {
          searchInput: searchInput.trim(),
          type: searchType,
        }
      );
      if (res.data.message) {
        // console.log("result:" + JSON.stringify(res.data.message));
        setSearchResults(res.data.message);
        setActiveRow(0); // Reset the active row index when search results change
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      console.log(err);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced search function
  const debouncedSearch = useCallback(() => {
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout for debounced search
    debounceTimeoutRef.current = setTimeout(() => {
      onHandleSearchInput();
    }, 300); // 300ms delay
  }, [searchInput, searchType]);

  // Effect to trigger debounced search when searchInput or searchType changes
  useEffect(() => {
    debouncedSearch();

    // Cleanup timeout on unmount or when searchInput/searchType changes
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchInput, searchType, debouncedSearch]);

  const handleKeyUp = async (event) => {
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveRow((prevActiveRow) =>
        prevActiveRow - 1 >= 0 ? prevActiveRow - 1 : 0
      );
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveRow((prevActiveRow) =>
        prevActiveRow + 1 < searchResults.length
          ? prevActiveRow + 1
          : prevActiveRow
      );
    } else if (event.key === "Enter") {
      event.preventDefault();
      try {
        const res = await axios.post(`${API_BASE_URL}/api/getOsByAccountCode`, {
          accountCode: searchResults[activeRow]?.account_code,
        });
        osValue = res.data.os || 0;
      } catch (err) {
        console.log(err);
      }
      updateFormData((prevFormData) => ({
        ...prevFormData,
        name: searchResults[activeRow]?.account_code || "",
        phoneNo: searchResults[activeRow]?.phone_no || "",
        altPhoneNo: searchResults[activeRow]?.alt_phone_no || "",
        city: searchResults[activeRow]?.city || "",
        address: searchResults[activeRow]?.address || "",
        os: osValue,
      }));
      handleClose();
    }
    // Removed the else clause that was calling onHandleSearchInput directly
  };

  useEffect(() => {
    setSearchResults([]); // Clear search results when modal opens or closes
    setActiveRow(0); // Reset the active row index when modal opens or closes
    setSearchInput(""); // Clear the search input when modal opens or closes
    setSearchType(type);
    setIsLoading(false); // Reset loading state
    // Clear any pending debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    if (showModal && inputRef.current) {
      inputRef.current.focus(); // Focus the input field when the modal is opened
    }
  }, [showModal, type]);

  useEffect(() => {
    if (tableRef.current) {
      // Scroll to the active row in the table
      const rowElement = tableRef.current.querySelector(
        `tr[data-index="${activeRow}"]`
      );
      if (rowElement) {
        rowElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }, [activeRow]);

  const handleCloseModal = useCallback(() => {
    setShowAddModal(false);
  }, []);

  const onClickAdd = () => {
    setShowAddModal(true);
  };

  const handleClickRow = (index) => {
    setActiveRow(index);
    inputRef.current.focus();
  };

  const onClickSwith = useCallback(() => {
    if (searchType === "purchase") {
      setSearchType("sales");
    } else if (searchType === "sales") {
      setSearchType("purchase");
    }
    setSearchInput("");
    setSearchResults([]);
    inputRef.current.focus();
  }, [searchType]);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === "F12") {
        onClickSwith();
      }
    };

    document.addEventListener("keydown", handleKeyPress);

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [onClickSwith]);

  return (
    <Fragment>
      <OuterBodyModal
        modalTitle={modalTitle}
        showModal={showModal}
        handleClose={handleClose}
      >
        <Row>
          <Col xs={12}>
            <Button onClick={onClickSwith}>
              {searchType === "purchase"
                ? "Switch to Customer"
                : "Switch to Vendor"}
            </Button>
          </Col>
          <Col xs={10} className="mt-2">
            <Form.Control
              type="text"
              placeholder="Search by Account Code or Phone No (min 2 characters)"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              onKeyUp={handleKeyUp}
              ref={inputRef}
            />
          </Col>
          <Col xs={2}>
            <Button onClick={onClickAdd}>+</Button>
          </Col>
        </Row>
        <div className="table-responsive mt-3">
          <table className="table table-striped text-center" ref={tableRef}>
            <thead className="table-info">
              <tr>
                <th>Account Code</th>
                <th>Phone No</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="2" className="text-center">
                    <div
                      className="spinner-border spinner-border-sm"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="ms-2">Searching...</span>
                  </td>
                </tr>
              ) : searchInput.trim().length < 2 ? (
                <tr>
                  <td colSpan="2" className="text-center text-muted">
                    Type at least 2 characters to search
                  </td>
                </tr>
              ) : searchResults.length === 0 ? (
                <tr>
                  <td colSpan="2" className="text-center text-muted">
                    No results found
                  </td>
                </tr>
              ) : (
                searchResults.map((result, index) => (
                  <tr
                    key={index}
                    data-index={index}
                    className={`${
                      index === activeRow ? styles["active-row"] : ""
                    } ${styles["all-rows"]}`}
                    onClick={() => handleClickRow(index)}
                  >
                    <td>{result.account_code}</td>
                    <td>{result.phone_no}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </OuterBodyModal>
      <OuterBodyModal
        modalTitle="Add Vendor"
        showModal={showAddModal}
        handleClose={handleCloseModal}
      >
        {type === "purchase" ? (
          <AddEditVendor
            initialFormData={initialFormData}
            type="Add Vendor"
            onHandleSubmit={handleCloseModal}
          ></AddEditVendor>
        ) : (
          <AddEditCustomer
            initialFormData={initialFormData}
            type="Add Customer"
            onHandleSubmit={handleCloseModal}
          ></AddEditCustomer>
        )}
      </OuterBodyModal>
    </Fragment>
  );
};

export default SearchCustomerModal;
